import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { PlusIcon, Trash01Icon as TrashIcon } from '@untitledui-icons/react/line'
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal'
import { Button } from '@/components/base/buttons'
import { Input } from '@/components/ui/Input'
import { Switch } from '@/components/ui/Switch'
import { TextArea } from '@/components/base/textarea/textarea'
import { cn } from '@/lib/utils'
import type { TransferRule } from './TransferFlowCard'
import { AvailabilityScheduleEditor } from './AvailabilityScheduleEditor'
import {
  findDuplicatePeriodIndexes,
  hasOverlappingOverrides,
  isOverrideActive,
  isOverrideExpired,
  isValidOverridePeriod,
  mergeLegacyClosedDates,
  overrideHasFutureEffect,
  sortOverridesByStartDate,
  todayISO,
  weekdaysInPeriod,
  type AvailabilitySchedule,
  type TransferOverride,
} from './transferOverrides'

const PHONE_REGEX = /^\+?[1-9]\d{1,14}$/

// Ephemeral UI identity for override cards so React keys stay stable when the
// list is reordered or items are deleted. Never persisted: handleSave rebuilds
// each override from its known fields only.
type EditableOverride = TransferOverride & { _uiId?: string }

let uiIdCounter = 0
const nextUiId = () => `override-${++uiIdCounter}`

const getDefaultRule = (t: (key: string) => string): TransferRule => ({
  number: '',
  reasons: [''],
  availability: [{
    days: [],
    start_time: '09:00',
    end_time: '17:00'
  }],
  closed_dates: [],
  closed_instruction: t('resources.assistants.transfer_flows.modal.closed_instruction_default'),
  unavailable_instruction: t('resources.assistants.transfer_flows.modal.unavailable_instruction_default'),
  overrides: []
})

interface TransferFlowModalProps {
  isOpen: boolean
  onClose: () => void
  rule: TransferRule | null
  index: number
  onSave: (rule: TransferRule, index: number) => void
}

interface ValidationErrors {
  number?: string
  reasons?: string
  availability?: Array<{ index: number; error: string }>
  overrides?: Array<{ index: number; error: string }>
}

export function TransferFlowModal({ isOpen, onClose, rule, index, onSave }: TransferFlowModalProps) {
  const { t, i18n } = useTranslation()
  const [currentRule, setCurrentRule] = useState<TransferRule>(() => getDefaultRule(t))
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [showExpired, setShowExpired] = useState(false)
  // Bumped on every modal (re)load so the schedule editors remount and
  // re-derive their internal 24h-mode state from the freshly loaded rule.
  const [editorSession, setEditorSession] = useState(0)
  const today = todayISO()

  useEffect(() => {
    if (rule) {
      const preparedRule = {
        ...getDefaultRule(t),
        ...rule,
        reasons: Array.isArray(rule.reasons) && rule.reasons.length > 0
          ? rule.reasons
          : [''],
        availability: Array.isArray(rule.availability) && rule.availability.length > 0
          ? rule.availability.map(schedule => ({
              ...schedule,
              days: schedule.days.map(day => Number(day))
            }))
          : getDefaultRule(t).availability,
        // Legacy single closed dates are edited as one-day closed exception periods
        overrides: sortOverridesByStartDate(
          mergeLegacyClosedDates(
            Array.isArray(rule.overrides) ? rule.overrides : [],
            rule.closed_dates
          )
        ).map((override) => ({ ...override, _uiId: nextUiId() })),
        closed_dates: []
      }
      setCurrentRule(preparedRule)
    } else {
      setCurrentRule(getDefaultRule(t))
    }
    setErrors({})
    setShowExpired(false)
    setEditorSession((session) => session + 1)
  }, [rule, isOpen, t])

  const validate = (): boolean => {
    const newErrors: ValidationErrors = {}

    if (!currentRule.number) {
      newErrors.number = t('resources.assistants.transfer_flows.modal.error_phone_required')
    } else if (!PHONE_REGEX.test(currentRule.number)) {
      newErrors.number = t('resources.assistants.transfer_flows.modal.error_phone_invalid')
    }

    if (!currentRule.reasons?.some(reason => reason && reason.trim())) {
      newErrors.reasons = t('resources.assistants.transfer_flows.modal.error_reason_required')
    }

    const availabilityErrors: Array<{ index: number; error: string }> = []
    currentRule.availability?.forEach((schedule, idx) => {
      if (!schedule.days || schedule.days.length === 0) {
        availabilityErrors.push({ index: idx, error: t('resources.assistants.transfer_flows.modal.error_days_required') })
      } else if (!schedule.start_time || !schedule.end_time) {
        availabilityErrors.push({ index: idx, error: t('resources.assistants.transfer_flows.modal.error_times_required') })
      } else if (schedule.number && !PHONE_REGEX.test(schedule.number)) {
        availabilityErrors.push({ index: idx, error: t('resources.assistants.transfer_flows.modal.error_phone_invalid') })
      }
    })
    if (availabilityErrors.length > 0) {
      newErrors.availability = availabilityErrors
    }

    const overrideErrors: Array<{ index: number; error: string }> = []
    currentRule.overrides?.forEach((override, idx) => {
      // Expired periods can no longer affect the future and their cards may be
      // hidden, so blocking on them would fail the save invisibly
      if (isOverrideExpired(override, today)) return
      if (!override.start_date || !override.end_date) {
        overrideErrors.push({ index: idx, error: t('resources.assistants.transfer_flows.modal.error_override_dates_required') })
      } else if (override.end_date < override.start_date && !override.recurring) {
        overrideErrors.push({ index: idx, error: t('resources.assistants.transfer_flows.modal.error_override_dates_order') })
      } else if (
        override.mode === 'custom' &&
        (!override.availability ||
          override.availability.length === 0 ||
          override.availability.some(schedule => !schedule.days || schedule.days.length === 0))
      ) {
        overrideErrors.push({ index: idx, error: t('resources.assistants.transfer_flows.modal.error_override_custom_schedule_required') })
      } else if (
        override.mode === 'custom' &&
        override.availability?.some(schedule => !schedule.start_time || !schedule.end_time)
      ) {
        overrideErrors.push({ index: idx, error: t('resources.assistants.transfer_flows.modal.error_times_required') })
      } else if (
        override.mode === 'custom' &&
        override.availability?.some(schedule => schedule.number && !PHONE_REGEX.test(schedule.number))
      ) {
        overrideErrors.push({ index: idx, error: t('resources.assistants.transfer_flows.modal.error_phone_invalid') })
      }
    })
    findDuplicatePeriodIndexes(currentRule.overrides ?? [], today).forEach((idx) => {
      if (!overrideErrors.some(e => e.index === idx)) {
        overrideErrors.push({ index: idx, error: t('resources.assistants.transfer_flows.modal.error_override_duplicate_period') })
      }
    })
    if (overrideErrors.length > 0) {
      newErrors.overrides = overrideErrors
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const formatSchedule = (schedule: AvailabilitySchedule): AvailabilitySchedule => {
    const formatted: AvailabilitySchedule = {
      days: schedule.days.map(day => Number(day)),
      start_time: schedule.start_time,
      end_time: schedule.end_time
    }
    if (schedule.number && schedule.number.trim()) {
      formatted.number = schedule.number.trim()
    }
    return formatted
  }

  const handleSave = () => {
    if (!validate()) return

    const formattedRule: TransferRule = {
      number: currentRule.number,
      reasons: currentRule.reasons.filter(reason => reason && reason.trim()),
      availability: currentRule.availability.map(formatSchedule),
      closed_dates: [],
      closed_instruction: currentRule.closed_instruction,
      unavailable_instruction: currentRule.unavailable_instruction,
      overrides: sortOverridesByStartDate(currentRule.overrides ?? []).map(override => {
        const formatted: TransferOverride = {
          start_date: override.start_date,
          end_date: override.end_date,
          mode: override.mode
        }
        if (override.label && override.label.trim()) {
          formatted.label = override.label.trim()
        }
        if (override.recurring) {
          formatted.recurring = true
        }
        if (override.mode === 'custom') {
          formatted.availability = (override.availability ?? []).map(formatSchedule)
        }
        if (override.closed_instruction && override.closed_instruction.trim()) {
          formatted.closed_instruction = override.closed_instruction.trim()
        }
        return formatted
      })
    }

    onSave(formattedRule, index)
    onClose()
  }

  const handleReasonChange = (idx: number, value: string) => {
    const updatedReasons = [...currentRule.reasons]
    updatedReasons[idx] = value
    setCurrentRule({ ...currentRule, reasons: updatedReasons })
  }

  const handleAddReason = () => {
    setCurrentRule({ ...currentRule, reasons: [...currentRule.reasons, ''] })
  }

  const handleRemoveReason = (idx: number) => {
    const updatedReasons = currentRule.reasons.filter((_, i) => i !== idx)
    setCurrentRule({ ...currentRule, reasons: updatedReasons.length > 0 ? updatedReasons : [''] })
  }

  const updateOverride = (idx: number, patch: Partial<TransferOverride>) => {
    const updatedOverrides = [...(currentRule.overrides ?? [])]
    let next = { ...updatedOverrides[idx], ...patch }
    const periodChanged =
      patch.start_date !== undefined || patch.end_date !== undefined || patch.recurring !== undefined
    if (periodChanged && next.availability) {
      if (next.recurring) {
        // A recurring period falls on different weekdays every year, so a
        // day restriction from the previous state must not stick around
        if (patch.recurring === true) {
          next = {
            ...next,
            availability: next.availability.map(schedule => ({ ...schedule, days: [1, 2, 3, 4, 5, 6, 7] }))
          }
        }
      } else {
        // Keep custom schedules within the days that occur in the period
        const selectable = weekdaysInPeriod(next.start_date, next.end_date)
        if (selectable.length < 7) {
          next = {
            ...next,
            availability: next.availability.map(schedule => {
              const days = schedule.days.filter(day => selectable.includes(day))
              return { ...schedule, days: days.length > 0 ? days : [...selectable] }
            })
          }
        }
      }
    }
    updatedOverrides[idx] = next
    setCurrentRule({ ...currentRule, overrides: updatedOverrides })
  }

  const handleAddOverride = () => {
    const newOverride: EditableOverride = {
      start_date: today,
      end_date: today,
      mode: 'closed',
      _uiId: nextUiId()
    }
    setCurrentRule({
      ...currentRule,
      overrides: [...(currentRule.overrides ?? []), newOverride]
    })
  }

  const handleRemoveOverride = (idx: number) => {
    setCurrentRule({
      ...currentRule,
      overrides: (currentRule.overrides ?? []).filter((_, i) => i !== idx)
    })
    // Errors are index-keyed; dropping them prevents a stale error from
    // attaching to the card that shifts into the removed slot
    setErrors((prev) => ({ ...prev, overrides: undefined }))
  }

  const handleOverrideModeChange = (idx: number, mode: TransferOverride['mode']) => {
    const override = (currentRule.overrides ?? [])[idx]
    const patch: Partial<TransferOverride> = { mode }
    if (mode === 'custom' && (!override.availability || override.availability.length === 0)) {
      const selectable = weekdaysInPeriod(override.start_date, override.end_date)
      patch.availability = [{
        days: selectable.length < 7 ? selectable : [1, 2, 3, 4, 5],
        start_time: '09:00',
        end_time: '17:00'
      }]
    }
    updateOverride(idx, patch)
  }

  // Day + month without a year, for recurring summaries
  const formatDayMonth = (iso: string) => {
    const [, month, day] = iso.split('-').map(Number)
    if (!month || !day) return iso
    return new Date(2000, month - 1, day).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' })
  }

  const overrideEntries = (currentRule.overrides ?? []).map((override, idx) => ({
    override: override as EditableOverride,
    idx
  }))
  const nonExpiredOverrides = overrideEntries
    .filter(({ override }) => !isOverrideExpired(override, today))
    .map(({ override }) => override)
  const expiredCount = overrideEntries.length - nonExpiredOverrides.length
  const visibleOverrideEntries = showExpired
    ? overrideEntries
    : overrideEntries.filter(({ override }) => !isOverrideExpired(override, today))

  return (
    <Modal isOpen={isOpen} onOpenChange={(open) => !open && onClose()} size="4xl">
      <ModalContent>
        <ModalHeader onClose={onClose}>
          {index >= 0 ? t('resources.assistants.transfer_flows.modal.edit_title') : t('resources.assistants.transfer_flows.modal.add_title')}
        </ModalHeader>

        <ModalBody className="max-h-[60vh] overflow-y-auto space-y-6">
          {/* Transfer Reasons */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
              {t('resources.assistants.transfer_flows.modal.reasons')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              {t('resources.assistants.transfer_flows.modal.reasons_description')}
            </p>
            {currentRule.reasons.map((reason, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-2">
                <Input
                  value={reason}
                  onChange={(value) => handleReasonChange(idx, value)}
                  placeholder={t('resources.assistants.transfer_flows.modal.reason_placeholder')}
                  className="flex-1"
                />
                <Button
                  color="tertiary"
                  size="sm"
                  onPress={() => handleRemoveReason(idx)}
                  isDisabled={currentRule.reasons.length <= 1}
                  className="p-2"
                  iconLeading={TrashIcon}
                />
              </div>
            ))}
            <Button color="secondary" size="sm" onPress={handleAddReason} iconLeading={PlusIcon}>
              {t('resources.assistants.transfer_flows.modal.add_reason')}
            </Button>
            {errors.reasons && (
              <p className="mt-1 text-sm text-error-600">{errors.reasons}</p>
            )}
          </div>

          {/* Transfer Destination */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
              {t('resources.assistants.transfer_flows.modal.transfer_to')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              {t('resources.assistants.transfer_flows.modal.transfer_to_description')}
            </p>
            <Input
              value={currentRule.number}
              onChange={(value) => setCurrentRule({ ...currentRule, number: value })}
              placeholder={t('resources.assistants.transfer_flows.modal.phone_placeholder')}
            />
            {errors.number && (
              <p className="mt-1 text-sm text-error-600">{errors.number}</p>
            )}
          </div>

          {/* Instructions */}
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                {t('resources.assistants.transfer_flows.modal.destination_closed')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                {t('resources.assistants.transfer_flows.modal.destination_closed_description')}
              </p>
              <TextArea
                value={currentRule.closed_instruction}
                onChange={(value) => setCurrentRule({ ...currentRule, closed_instruction: value })}
                rows={3}
              />
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                {t('resources.assistants.transfer_flows.modal.destination_unavailable')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                {t('resources.assistants.transfer_flows.modal.destination_unavailable_description')}
              </p>
              <TextArea
                value={currentRule.unavailable_instruction}
                onChange={(value) => setCurrentRule({ ...currentRule, unavailable_instruction: value })}
                rows={3}
              />
            </div>
          </div>

          {/* Availability */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
              {t('resources.assistants.transfer_flows.modal.availability')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              {t('resources.assistants.transfer_flows.modal.availability_description')}
            </p>

            {errors.availability && errors.availability.length > 0 && (
              <div className="mb-3 rounded-lg bg-error-50 p-3 text-sm text-error-700 dark:bg-error-900/20 dark:text-error-400">
                {t('resources.assistants.transfer_flows.modal.error_schedule_missing')}
              </div>
            )}

            <AvailabilityScheduleEditor
              key={`availability-${editorSession}`}
              schedules={currentRule.availability}
              onChange={(availability) => setCurrentRule({ ...currentRule, availability })}
              errors={errors.availability}
            />
          </div>

          {/* Exception Periods */}
          <div>
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
              {t('resources.assistants.transfer_flows.modal.overrides')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
              {t('resources.assistants.transfer_flows.modal.overrides_description')}
            </p>

            {errors.overrides && errors.overrides.length > 0 && (
              <div className="mb-3 rounded-lg bg-error-50 p-3 text-sm text-error-700 dark:bg-error-900/20 dark:text-error-400">
                {t('resources.assistants.transfer_flows.modal.error_overrides_invalid')}
              </div>
            )}

            {hasOverlappingOverrides(nonExpiredOverrides) && (
              <div className="mb-3 rounded-lg bg-warning-50 p-3 text-sm text-warning-700 dark:bg-warning-900/20 dark:text-warning-400">
                {t('resources.assistants.transfer_flows.modal.override_overlap_warning')}
              </div>
            )}

            {visibleOverrideEntries.map(({ override, idx }, position) => {
              const expired = isOverrideExpired(override, today)
              const active = isOverrideActive(override, today)
              const noEffect = !overrideHasFutureEffect(currentRule.overrides ?? [], idx, today)
              const overrideError = errors.overrides?.find(e => e.index === idx)?.error

              return (
                <div
                  key={override._uiId ?? `idx-${idx}`}
                  className={cn(
                    'mb-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700',
                    expired && 'opacity-60'
                  )}
                >
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('resources.assistants.transfer_flows.modal.override_number_title', { number: position + 1 })}
                      </span>
                      {active && (
                        <span className="inline-flex items-center rounded-full bg-warning-50 px-2 py-0.5 text-xs font-medium text-warning-700 dark:bg-warning-900/20 dark:text-warning-400">
                          {t('resources.assistants.transfer_flows.modal.override_active_now')}
                        </span>
                      )}
                      {expired && (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                          {t('resources.assistants.transfer_flows.modal.override_expired')}
                        </span>
                      )}
                    </div>
                    <Button
                      color="tertiary"
                      size="sm"
                      onPress={() => handleRemoveOverride(idx)}
                      className="p-1"
                      iconLeading={TrashIcon}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                      {t('resources.assistants.transfer_flows.modal.override_label')}
                    </label>
                    <Input
                      value={override.label ?? ''}
                      onChange={(value) => updateOverride(idx, { label: value })}
                      placeholder={t('resources.assistants.transfer_flows.modal.override_label_placeholder')}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        {t('resources.assistants.transfer_flows.modal.override_start_date')}
                      </label>
                      <Input
                        type="date"
                        value={override.start_date}
                        onChange={(value) => updateOverride(idx, { start_date: value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                        {t('resources.assistants.transfer_flows.modal.override_end_date')}
                      </label>
                      <Input
                        type="date"
                        value={override.end_date}
                        onChange={(value) => updateOverride(idx, { end_date: value })}
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center justify-between">
                      <label className="text-sm text-gray-600 dark:text-gray-400">
                        {t('resources.assistants.transfer_flows.modal.override_recurring')}
                      </label>
                      <Switch
                        isSelected={!!override.recurring}
                        onChange={(value) => updateOverride(idx, { recurring: value })}
                      />
                    </div>
                    {override.recurring && isValidOverridePeriod(override) && (
                      <p className="mt-1 text-sm text-brand-600 dark:text-brand-400">
                        {formatDayMonth(override.start_date) === formatDayMonth(override.end_date)
                          ? t('resources.assistants.transfer_flows.modal.override_recurring_summary_single', {
                              date: formatDayMonth(override.start_date)
                            })
                          : t('resources.assistants.transfer_flows.modal.override_recurring_summary', {
                              start: formatDayMonth(override.start_date),
                              end: formatDayMonth(override.end_date)
                            })}
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {t('resources.assistants.transfer_flows.modal.override_mode')}
                    </label>
                    <div className="flex gap-2">
                      {(['closed', 'custom'] as const).map((mode) => (
                        <button
                          key={mode}
                          type="button"
                          onClick={() => handleOverrideModeChange(idx, mode)}
                          className={cn(
                            'px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
                            override.mode === mode
                              ? 'bg-brand-500 text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                          )}
                        >
                          {t(`resources.assistants.transfer_flows.modal.override_mode_${mode}`)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {override.mode === 'custom' && (
                    <div className="mb-4">
                      <AvailabilityScheduleEditor
                        schedules={override.availability ?? []}
                        onChange={(availability) => updateOverride(idx, { availability })}
                        selectableDays={
                          override.recurring ? undefined : weekdaysInPeriod(override.start_date, override.end_date)
                        }
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                      {t('resources.assistants.transfer_flows.modal.override_instruction')}
                    </label>
                    <TextArea
                      value={override.closed_instruction ?? ''}
                      onChange={(value) => updateOverride(idx, { closed_instruction: value })}
                      placeholder={t('resources.assistants.transfer_flows.modal.override_instruction_placeholder')}
                      rows={2}
                    />
                  </div>

                  {noEffect && (
                    <p className="mt-2 text-sm text-warning-600 dark:text-warning-400">
                      {t('resources.assistants.transfer_flows.modal.override_no_effect_warning')}
                    </p>
                  )}
                  {overrideError && (
                    <p className="mt-2 text-sm text-error-600">{overrideError}</p>
                  )}
                </div>
              )
            })}

            <div className="flex items-center gap-3">
              <Button color="secondary" size="sm" onPress={handleAddOverride} iconLeading={PlusIcon}>
                {t('resources.assistants.transfer_flows.modal.add_override')}
              </Button>
              {expiredCount > 0 && (
                <Button color="link-color" size="sm" onPress={() => setShowExpired(!showExpired)}>
                  {showExpired
                    ? t('resources.assistants.transfer_flows.modal.hide_expired_overrides')
                    : t('resources.assistants.transfer_flows.modal.show_expired_overrides', { count: expiredCount })}
                </Button>
              )}
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onPress={onClose}>
            {t('resources.generic.cancel')}
          </Button>
          <Button color="primary" onPress={handleSave}>
            {t('resources.generic.save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default TransferFlowModal
